import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import {
  Plus,
  Search,
  MoreVertical,
  Pencil,
  Trash2,
  Truck,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Loader2,
  Filter,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ArrowLeftRight,
  X
} from 'lucide-react';

// Status configuration
const STATUS_CONFIG = {
  ACTIVE: { label: 'Active', color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle2 },
  UNAVAILABLE: { label: 'Unavailable', color: 'bg-yellow-100 text-yellow-700 border-yellow-200', icon: AlertTriangle },
  OUT_OF_SERVICE: { label: 'Out of Service', color: 'bg-red-100 text-red-700 border-red-200', icon: XCircle },
  IN_MAINTENANCE: { label: 'In Maintenance', color: 'bg-orange-100 text-orange-700 border-orange-200', icon: AlertTriangle },
  RETIRED: { label: 'Retired', color: 'bg-gray-100 text-gray-700 border-gray-200', icon: XCircle },
};

const STATUS_OPTIONS = Object.keys(STATUS_CONFIG).map(key => ({
  value: key,
  label: STATUS_CONFIG[key].label
}));

const FUEL_TYPES = ['GAS', 'DIESEL', 'ELECTRIC', 'HYBRID'];

const VehiclesPage = () => {
  const navigate = useNavigate();
  const { api, currentFacility, hasRole, user } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [facilities, setFacilities] = useState([]);
  // 2026-07-19 Phase 1 scoping — Add/Edit uses `facilities` (SCOPED to user's
  // access). The Transfer flow keeps a separate `allFacilities` list so vehicles
  // can still be moved to any org facility per existing product spec.
  const [allFacilities, setAllFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  
  // Sort state
  const [sort, setSort] = useState({ field: 'display_id', direction: 'asc' });
  
  // Filter state (multi-select for each field)
  const [filters, setFilters] = useState({
    status: [],
    fuel_type: []
  });
  
  // Modal states
  const [modalOpen, setModalOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Transfer/Loan modal states
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [transferVehicle, setTransferVehicle] = useState(null);
  const [transferFacilityId, setTransferFacilityId] = useState('');
  const [transferring, setTransferring] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    facility_id: '',
    display_id: '',
    license_plate: '',
    vin: '',
    year: new Date().getFullYear(),
    make: '',
    model: '',
    fuel_type: 'DIESEL',
    registration: '',
    vehicle_group: '',
    associated_driver: '',
    status: 'ACTIVE'
  });

  // Role-based permissions
  const isAdmin = hasRole(['ADMIN']);
  const isManager = hasRole(['MANAGER']);
  const isOperator = hasRole(['OPERATOR']);
  const isTechnician = hasRole(['TECHNICIAN']);
  
  // Permission checks
  const canCreate = isAdmin || isManager;  // Admin and Manager can create
  const canEdit = isAdmin || isManager || isOperator;  // Admin, Manager, and Operator can edit
  const canEditVin = isAdmin || isManager;  // Only Admin and Manager can change VIN
  const canDelete = isAdmin;  // Only Admin can delete
  const canTransfer = isAdmin || isManager || isOperator;  // Admin, Manager, and Operator can transfer
  const canView = isAdmin || isManager || isOperator;  // Technicians cannot view

  // Redirect Technicians away from this page
  useEffect(() => {
    if (isTechnician) {
      toast.error('You do not have access to the Vehicles page');
      navigate('/work-schedules');
    }
  }, [isTechnician, navigate]);

  const fetchVehicles = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (currentFacility && currentFacility !== 'all') {
        params.facility_id = currentFacility;
      }
      const response = await api.get('/vehicles', { params });
      setVehicles(response.data);
    } catch (error) {
      console.error('Failed to fetch vehicles:', error);
      toast.error('Failed to load vehicles');
    } finally {
      setLoading(false);
    }
  }, [api, currentFacility]);

  const fetchFacilities = useCallback(async () => {
    try {
      // Scoped list — respects role (admin=all, manager=assigned, operator=self).
      // Used by the Add/Edit Vehicle facility dropdown so managers can't leak
      // vehicles to facilities outside their scope.
      const scopedRes = await api.get('/facilities');
      setFacilities(scopedRes.data);
      // Unscoped org-wide list ONLY for the vehicle Transfer modal, which
      // needs to see all facilities per existing behavior.
      const allRes = await api.get('/facilities', { params: { for_transfer: true } });
      setAllFacilities(allRes.data);
    } catch (error) {
      console.error('Failed to fetch facilities:', error);
    }
  }, [api]);

  useEffect(() => {
    if (canView) {
      fetchVehicles();
      fetchFacilities();
    }
  }, [currentFacility, canView, fetchVehicles, fetchFacilities]);

  // Filter and sort vehicles
  const filteredAndSortedVehicles = vehicles
    .filter(vehicle => {
      // Search filter
      if (search) {
        const searchLower = search.toLowerCase();
        const matchesSearch = 
          vehicle.display_id?.toLowerCase().includes(searchLower) ||
          vehicle.license_plate?.toLowerCase().includes(searchLower) ||
          vehicle.vin?.toLowerCase().includes(searchLower) ||
          vehicle.make?.toLowerCase().includes(searchLower) ||
          vehicle.model?.toLowerCase().includes(searchLower) ||
          vehicle.vehicle_group?.toLowerCase().includes(searchLower) ||
          vehicle.associated_driver?.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }
      
      // Status filter
      if (filters.status.length > 0 && !filters.status.includes(vehicle.status)) {
        return false;
      }
      
      // Fuel type filter
      if (filters.fuel_type.length > 0 && !filters.fuel_type.includes(vehicle.fuel_type)) {
        return false;
      }
      
      return true;
    })
    .sort((a, b) => {
      const { field, direction } = sort;
      let aVal = a[field];
      let bVal = b[field];
      
      // Handle nested fields
      if (field === 'facility') {
        aVal = a.facility_name || '';
        bVal = b.facility_name || '';
      }
      
      // String comparison
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = (bVal || '').toLowerCase();
      }
      
      if (aVal < bVal) return direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return direction === 'asc' ? 1 : -1;
      return 0;
    });

  const toggleSort = (field) => {
    setSort(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const SortIcon = ({ field }) => {
    if (sort.field !== field) return <ArrowUpDown className="w-4 h-4 text-[#808080]" />;
    return sort.direction === 'asc' 
      ? <ArrowUp className="w-4 h-4 text-[#005EF8]" />
      : <ArrowDown className="w-4 h-4 text-[#005EF8]" />;
  };

  const toggleFilter = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: prev[filterType].includes(value)
        ? prev[filterType].filter(v => v !== value)
        : [...prev[filterType], value]
    }));
  };

  const clearFilters = () => {
    setFilters({ status: [], fuel_type: [] });
  };

  const activeFilterCount = filters.status.length + filters.fuel_type.length;

  const resetForm = () => {
    setFormData({
      facility_id: currentFacility && currentFacility !== 'all' ? currentFacility : '',
      display_id: '',
      license_plate: '',
      vin: '',
      year: new Date().getFullYear(),
      make: '',
      model: '',
      fuel_type: 'DIESEL',
      registration: '',
      vehicle_group: '',
      associated_driver: '',
      status: 'ACTIVE'
    });
    setEditingVehicle(null);
  };

  const openAddModal = () => {
    resetForm();
    setModalOpen(true);
  };

  const openEditModal = (vehicle) => {
    setEditingVehicle(vehicle);
    setFormData({
      facility_id: vehicle.facility_id || '',
      display_id: vehicle.display_id || '',
      license_plate: vehicle.license_plate || '',
      vin: vehicle.vin || '',
      year: vehicle.year || new Date().getFullYear(),
      make: vehicle.make || '',
      model: vehicle.model || '',
      fuel_type: vehicle.fuel_type || 'DIESEL',
      registration: vehicle.registration || '',
      vehicle_group: vehicle.vehicle_group || '',
      associated_driver: vehicle.associated_driver || '',
      status: vehicle.status || 'ACTIVE'
    });
    setModalOpen(true);
  };

  const openTransferModal = (vehicle) => {
    setTransferVehicle(vehicle);
    setTransferFacilityId('');
    setTransferModalOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.facility_id) {
      toast.error('Please select a facility');
      return;
    }
    if (!formData.display_id.trim()) {
      toast.error('Please enter a display ID');
      return;
    }
    if (!formData.license_plate.trim()) {
      toast.error('Please enter a license plate');
      return;
    }
    if (!formData.vin.trim() || formData.vin.length !== 17) {
      toast.error('VIN must be exactly 17 characters');
      return;
    }
    if (!formData.make.trim()) {
      toast.error('Please enter the vehicle make');
      return;
    }
    if (!formData.model.trim()) {
      toast.error('Please enter the vehicle model');
      return;
    }
    // Registration is optional, but if entered must be in MM/YY format
    if (formData.registration && formData.registration.trim()) {
      if (!/^(0[1-9]|1[0-2])\/\d{2}$/.test(formData.registration.trim())) {
        toast.error('Registration must be in MM/YY format (e.g., 05/27)');
        return;
      }
    }
    
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        year: parseInt(formData.year),
        registration: formData.registration?.trim() || null,
        vehicle_group: formData.vehicle_group || null,
        associated_driver: formData.associated_driver || null
      };
      
      if (editingVehicle) {
        await api.put(`/vehicles/${editingVehicle.id}`, payload);
        toast.success('Vehicle updated successfully');
      } else {
        await api.post('/vehicles', payload);
        toast.success('Vehicle added successfully');
      }
      
      setModalOpen(false);
      resetForm();
      fetchVehicles();
    } catch (error) {
      console.error('Failed to save vehicle:', error);
      toast.error(error.response?.data?.detail || 'Failed to save vehicle');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/vehicles/${deleteId}`);
      toast.success('Vehicle deleted successfully');
      setDeleteId(null);
      fetchVehicles();
    } catch (error) {
      toast.error('Failed to delete vehicle');
    }
  };

  const handleTransfer = async () => {
    if (!transferVehicle || !transferFacilityId) {
      toast.error('Please select a facility to transfer to');
      return;
    }
    
    setTransferring(true);
    try {
      await api.put(`/vehicles/${transferVehicle.id}`, {
        ...transferVehicle,
        facility_id: transferFacilityId
      });
      toast.success('Vehicle transferred successfully');
      setTransferModalOpen(false);
      setTransferVehicle(null);
      setTransferFacilityId('');
      fetchVehicles();
    } catch (error) {
      toast.error('Failed to transfer vehicle');
    } finally {
      setTransferring(false);
    }
  };

  const getStatusConfig = (status) => STATUS_CONFIG[status] || STATUS_CONFIG.ACTIVE;

  // If user is Technician, don't render the page (redirect is handled in useEffect)
  if (isTechnician) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-[#323033] mb-2">Access Denied</h2>
          <p className="text-[#5A5A5A]">You do not have permission to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="vehicles-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#323033]">Vehicles</h1>
          <p className="text-[#5A5A5A] mt-1">
            {filteredAndSortedVehicles.length} vehicle{filteredAndSortedVehicles.length !== 1 ? 's' : ''}
            {activeFilterCount > 0 && ` (filtered)`}
          </p>
        </div>
        {canCreate && (
          <Button
            onClick={openAddModal}
            className="bg-[#005EF8] hover:bg-[#0047C2] text-white"
            data-testid="add-vehicle-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Vehicle
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
        {/* Search */}
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#808080]" />
          <Input
            placeholder="Search by ID, plate, VIN, make, model..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="search-input"
          />
        </div>

        {/* Filter Dropdowns */}
        <div className="flex gap-2 flex-wrap">
          {/* Status Filter */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="status-filter-btn">
                <Filter className="w-4 h-4" />
                Status
                {filters.status.length > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 bg-[#005EF8] text-white text-xs rounded-full">
                    {filters.status.length}
                  </span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-56" align="start">
              <div className="space-y-2">
                <p className="text-sm font-medium text-[#323033] mb-2">Filter by Status</p>
                {STATUS_OPTIONS.map(option => (
                  <label key={option.value} className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={filters.status.includes(option.value)}
                      onCheckedChange={() => toggleFilter('status', option.value)}
                      data-testid={`status-filter-${option.value.toLowerCase()}`}
                    />
                    <span className="text-sm">{option.label}</span>
                  </label>
                ))}
              </div>
            </PopoverContent>
          </Popover>

          {/* Fuel Type Filter */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="fuel-filter-btn">
                <Filter className="w-4 h-4" />
                Fuel Type
                {filters.fuel_type.length > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 bg-[#005EF8] text-white text-xs rounded-full">
                    {filters.fuel_type.length}
                  </span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-56" align="start">
              <div className="space-y-2">
                <p className="text-sm font-medium text-[#323033] mb-2">Filter by Fuel Type</p>
                {FUEL_TYPES.map(type => (
                  <label key={type} className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={filters.fuel_type.includes(type)}
                      onCheckedChange={() => toggleFilter('fuel_type', type)}
                      data-testid={`fuel-filter-${type.toLowerCase()}`}
                    />
                    <span className="text-sm">{type}</span>
                  </label>
                ))}
              </div>
            </PopoverContent>
          </Popover>

          {/* Clear Filters */}
          {activeFilterCount > 0 && (
            <Button
              variant="ghost"
              onClick={clearFilters}
              className="text-[#5A5A5A] hover:text-[#323033]"
              data-testid="clear-filters-btn"
            >
              <X className="w-4 h-4 mr-1" />
              Clear ({activeFilterCount})
            </Button>
          )}
        </div>
      </div>

      {/* Active Filters Display */}
      {activeFilterCount > 0 && (
        <div className="flex flex-wrap gap-2">
          {filters.status.map(status => (
            <span
              key={`active-${status}`}
              className="inline-flex items-center gap-1 px-2 py-1 bg-[#005EF8]/10 text-[#005EF8] text-sm rounded-full"
            >
              {getStatusConfig(status).label}
              <button onClick={() => toggleFilter('status', status)} className="hover:text-[#0047C2]">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
          {filters.fuel_type.map(type => (
            <span
              key={`active-fuel-${type}`}
              className="inline-flex items-center gap-1 px-2 py-1 bg-[#005EF8]/10 text-[#005EF8] text-sm rounded-full"
            >
              {type}
              <button onClick={() => toggleFilter('fuel_type', type)} className="hover:text-[#0047C2]">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#005EF8] mx-auto"></div>
              <p className="text-[#5A5A5A] mt-4">Loading...</p>
            </div>
          ) : filteredAndSortedVehicles.length === 0 ? (
            <div className="p-12 text-center">
              <Truck className="w-12 h-12 text-[#D3D5D4] mx-auto mb-4" />
              <h3 className="text-lg font-medium text-[#323033] mb-2">
                {vehicles.length === 0 ? 'No vehicles yet' : 'No matching vehicles'}
              </h3>
              <p className="text-[#5A5A5A] mb-6">
                {vehicles.length === 0 
                  ? 'Add your first vehicle to assign them to work schedules'
                  : 'Try adjusting your search or filters'
                }
              </p>
              {vehicles.length === 0 && canCreate && (
                <Button
                  onClick={openAddModal}
                  className="bg-[#005EF8] hover:bg-[#0047C2] text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Vehicle
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table" style={{ whiteSpace: 'nowrap', tableLayout: 'auto' }}>
                <thead>
                  <tr>
                    <th>
                      <button
                        onClick={() => toggleSort('display_id')}
                        className="flex items-center gap-1 hover:text-[#005EF8]"
                        data-testid="sort-display-id"
                      >
                        Display ID
                        <SortIcon field="display_id" />
                      </button>
                    </th>
                    <th>License Plate</th>
                    <th>VIN</th>
                    <th>Year / Make / Model</th>
                    <th>Fuel Type</th>
                    <th>Registration</th>
                    <th>Group</th>
                    <th>Driver</th>
                    {(isAdmin || isManager) && (
                      <th>
                        <button
                          onClick={() => toggleSort('facility')}
                          className="flex items-center gap-1 hover:text-[#005EF8]"
                          data-testid="sort-facility"
                        >
                          Facility
                          <SortIcon field="facility" />
                        </button>
                      </th>
                    )}
                    <th>
                      <button
                        onClick={() => toggleSort('status')}
                        className="flex items-center gap-1 hover:text-[#005EF8]"
                        data-testid="sort-status"
                      >
                        Status
                        <SortIcon field="status" />
                      </button>
                    </th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSortedVehicles.map((vehicle) => {
                    const statusConfig = getStatusConfig(vehicle.status);
                    const StatusIcon = statusConfig.icon;
                    return (
                      <tr key={vehicle.id} data-testid={`vehicle-row-${vehicle.id}`}>
                        <td>
                          <div className="flex items-center gap-2">
                            <Truck className="w-4 h-4 text-[#5A5A5A]" />
                            <span className="font-medium">{vehicle.display_id}</span>
                          </div>
                        </td>
                        <td className="font-medium">{vehicle.license_plate}</td>
                        <td className="font-mono text-sm text-[#5A5A5A]" title={vehicle.vin}>
                          {vehicle.vin ? `...${vehicle.vin.slice(-6)}` : '-'}
                        </td>
                        <td>
                          <span>{vehicle.year} {vehicle.make} {vehicle.model}</span>
                        </td>
                        <td>{vehicle.fuel_type}</td>
                        <td className="whitespace-nowrap">{vehicle.registration || 'N/A'}</td>
                        <td>{vehicle.vehicle_group || '-'}</td>
                        <td>{vehicle.associated_driver || '-'}</td>
                        {(isAdmin || isManager) && (
                          <td>{vehicle.facility_name || '-'}</td>
                        )}
                        <td>
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${statusConfig.color}`}>
                            <StatusIcon className="w-3 h-3" />
                            {statusConfig.label}
                          </span>
                        </td>
                        <td className="text-right">
                          {(canEdit || canTransfer) && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" data-testid={`vehicle-actions-${vehicle.id}`}>
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {canEdit && (
                                  <DropdownMenuItem onClick={() => openEditModal(vehicle)} data-testid={`edit-vehicle-${vehicle.id}`}>
                                    <Pencil className="w-4 h-4 mr-2" />
                                    Edit
                                  </DropdownMenuItem>
                                )}
                                {canTransfer && (
                                  <DropdownMenuItem onClick={() => openTransferModal(vehicle)} data-testid={`transfer-vehicle-${vehicle.id}`}>
                                    <ArrowLeftRight className="w-4 h-4 mr-2" />
                                    Transfer/Loan
                                  </DropdownMenuItem>
                                )}
                                {canDelete && (
                                  <DropdownMenuItem
                                    onClick={() => setDeleteId(vehicle.id)}
                                    className="text-red-600"
                                    data-testid={`delete-vehicle-${vehicle.id}`}
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Delete
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingVehicle ? 'Edit Vehicle' : 'Add Vehicle'}</DialogTitle>
            <DialogDescription>
              {editingVehicle ? 'Update vehicle details' : 'Add a new vehicle to your fleet'}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="facility_id">Facility *</Label>
                <Select value={formData.facility_id} onValueChange={(v) => handleSelectChange('facility_id', v)}>
                  <SelectTrigger data-testid="facility-select">
                    <SelectValue placeholder="Select facility..." />
                  </SelectTrigger>
                  <SelectContent>
                    {facilities.map((facility) => (
                      <SelectItem key={facility.id} value={facility.id}>
                        {facility.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="display_id">Display ID *</Label>
                <Input
                  id="display_id"
                  name="display_id"
                  value={formData.display_id}
                  onChange={handleInputChange}
                  placeholder="e.g., TRUCK-001"
                  required
                  data-testid="display-id-input"
                />
              </div>
              <div>
                <Label htmlFor="license_plate">License Plate *</Label>
                <Input
                  id="license_plate"
                  name="license_plate"
                  value={formData.license_plate}
                  onChange={handleInputChange}
                  placeholder="e.g., ABC-1234"
                  required
                  data-testid="license-plate-input"
                />
              </div>
              <div>
                <Label htmlFor="vin">VIN *</Label>
                <Input
                  id="vin"
                  name="vin"
                  value={formData.vin}
                  onChange={handleInputChange}
                  placeholder="17-character VIN"
                  maxLength={17}
                  required
                  disabled={editingVehicle && !canEditVin}
                  data-testid="vin-input"
                />
                {editingVehicle && !canEditVin && (
                  <p className="text-xs text-amber-600 mt-1">VIN can only be changed by a Manager or Admin</p>
                )}
                <p className="text-xs text-[#5A5A5A] mt-1">{formData.vin.length}/17 characters</p>
              </div>
              <div>
                <Label htmlFor="year">Year *</Label>
                <Input
                  id="year"
                  name="year"
                  type="number"
                  value={formData.year}
                  onChange={handleInputChange}
                  min={1900}
                  max={2100}
                  required
                  data-testid="year-input"
                />
              </div>
              <div>
                <Label htmlFor="make">Make *</Label>
                <Input
                  id="make"
                  name="make"
                  value={formData.make}
                  onChange={handleInputChange}
                  placeholder="e.g., Ford"
                  required
                  data-testid="make-input"
                />
              </div>
              <div>
                <Label htmlFor="model">Model *</Label>
                <Input
                  id="model"
                  name="model"
                  value={formData.model}
                  onChange={handleInputChange}
                  placeholder="e.g., F-350"
                  required
                  data-testid="model-input"
                />
              </div>
              <div>
                <Label htmlFor="fuel_type">Fuel Type *</Label>
                <Select value={formData.fuel_type} onValueChange={(v) => handleSelectChange('fuel_type', v)}>
                  <SelectTrigger data-testid="fuel-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FUEL_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="registration">Registration</Label>
                <Input
                  id="registration"
                  name="registration"
                  value={formData.registration}
                  onChange={handleInputChange}
                  placeholder="MM/YY (e.g., 05/27)"
                  maxLength={5}
                  data-testid="registration-input"
                />
              </div>
              <div>
                <Label htmlFor="vehicle_group">Vehicle Group</Label>
                <Input
                  id="vehicle_group"
                  name="vehicle_group"
                  value={formData.vehicle_group}
                  onChange={handleInputChange}
                  placeholder="e.g., Service Fleet"
                  data-testid="vehicle-group-input"
                />
              </div>
              <div>
                <Label htmlFor="associated_driver">Associated Driver</Label>
                <Input
                  id="associated_driver"
                  name="associated_driver"
                  value={formData.associated_driver}
                  onChange={handleInputChange}
                  placeholder="Driver name"
                  data-testid="associated-driver-input"
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="status">Status *</Label>
                <Select value={formData.status} onValueChange={(v) => handleSelectChange('status', v)}>
                  <SelectTrigger data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map(option => (
                      <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-[#005EF8] hover:bg-[#0047C2] text-white"
                disabled={submitting}
                data-testid="submit-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  editingVehicle ? 'Update Vehicle' : 'Add Vehicle'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Transfer/Loan Modal */}
      <Dialog open={transferModalOpen} onOpenChange={setTransferModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Transfer / Loan Vehicle</DialogTitle>
            <DialogDescription>
              Transfer {transferVehicle?.display_id} to another facility
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Current Facility</Label>
              <p className="text-sm text-[#5A5A5A] mt-1">
                {allFacilities.find(f => f.id === transferVehicle?.facility_id)?.name || '-'}
              </p>
            </div>
            
            <div>
              <Label htmlFor="transfer-facility">Transfer To *</Label>
              <Select value={transferFacilityId} onValueChange={setTransferFacilityId}>
                <SelectTrigger data-testid="transfer-facility-select">
                  <SelectValue placeholder="Select destination facility..." />
                </SelectTrigger>
                <SelectContent>
                  {allFacilities
                    .filter(f => f.id !== transferVehicle?.facility_id)
                    .map((facility) => (
                      <SelectItem key={facility.id} value={facility.id}>
                        {facility.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setTransferModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleTransfer}
              className="bg-[#005EF8] hover:bg-[#0047C2] text-white"
              disabled={transferring || !transferFacilityId}
              data-testid="confirm-transfer-btn"
            >
              {transferring ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Transferring...
                </>
              ) : (
                <>
                  <ArrowLeftRight className="w-4 h-4 mr-2" />
                  Transfer Vehicle
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Vehicle?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the vehicle from your fleet.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default VehiclesPage;
